// src/features/system/auth/Login.tsx
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { bff } from '../../../lib/http/bff';
import { useAuth } from '@/shared/auth';
import { useQueryClient } from '@tanstack/react-query';
import { authUiMessageFromError, type AuthUiMessage } from '@/shared/auth/authUiMessages';
import { clearAuthKickoutState, getAuthKickoutState } from '@/shared/auth/authKickout';
import { postAuthEvent, subscribeAuthEvents } from '@/shared/auth/authEvents';
import { resetAuthDisconnected } from '@/lib/auth/authDisconnect';
import {
  normalizePostLoginTarget as resolvePostLoginTarget,
  shouldForceTableOpsLanding,
} from '@/shared/auth/shouldForceTableOpsLanding';
import { X } from 'lucide-react';

type LoginProps = {
  inline?: boolean;
  from?: { pathname: string; search?: string; hash?: string };
  onClose?: () => void;
};

export default function Login(props: LoginProps) {
  useDocumentTitle('Login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState<AuthUiMessage | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [oidcPending, setOidcPending] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const { user, loading, refresh, capabilities, nav } = useAuth();
  const queryClient = useQueryClient();
  const attemptedAutoRecoverRef = React.useRef(false);

  const shouldForceTableopsLanding = shouldForceTableOpsLanding({ user, capabilities, nav });

  function normalizePostLoginTarget(raw: string): string {
    return resolvePostLoginTarget(raw, { user, capabilities, nav });
  }

  useEffect(() => {
    try {
      localStorage.removeItem('jwt');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('app_jwt');
    } catch {}
  }, []);

  useEffect(() => {
    const msg = (location.state as any)?.authMessage as AuthUiMessage | undefined;
    if (msg?.title || msg?.description) {
      setErrorMsg({
        title: String(msg.title || 'Sign-in required'),
        description: String(msg.description || 'Please sign in again.'),
      });
      return;
    }

    // If we were kicked out on a protected page, AuthDisconnectGate persists a message
    // so this Login view can show it even after a hard refresh.
    const kickout = getAuthKickoutState();
    if (kickout?.message?.title || kickout?.message?.description) {
      setErrorMsg(kickout.message);
    }
  }, [location.state]);

  // If another tab logs in, auto-recover this tab without asking for credentials again.
  // This is "best practice" for cookie/refresh-token based sessions: never store passwords.
  useEffect(() => {
    return subscribeAuthEvents(async (evt) => {
      if (evt.type !== 'login') return;
      if (props?.inline) {
        // Protected route will re-render automatically once `user` is set.
        void refresh(true);
        return;
      }

      try {
        await refresh(true);
        clearAuthKickoutState();
        resetAuthDisconnected();

        const from =
          (location.state as any)?.from ||
          props?.from ||
          getAuthKickoutState()?.from;

        const target = typeof from?.pathname === 'string'
          ? from.pathname + (from.search || '') + (from.hash || '')
          : '/';

        navigate(normalizePostLoginTarget(target), { replace: true });
      } catch {
        // ignore; user can still sign in manually
      }
    });
  }, [capabilities, location.state, nav, navigate, props?.from, props?.inline, refresh, user]);

  // Self-heal once: if we landed on /login but cookies already hold a valid
  // session, rehydrate auth state and bounce back. Do not retry endlessly.
  useEffect(() => {
    if (props?.inline) return;
    if (user || loading) return;
    if (attemptedAutoRecoverRef.current) return;
    attemptedAutoRecoverRef.current = true;
    void refresh(false);
  }, [loading, props?.inline, refresh, user]);

  useEffect(() => {
    if (props?.inline) return;
    if (!user) return;

    clearAuthKickoutState();
    resetAuthDisconnected();

    const from =
      (location.state as any)?.from ||
      props?.from ||
      getAuthKickoutState()?.from;

    const target = typeof from?.pathname === 'string'
      ? from.pathname + (from.search || '') + (from.hash || '')
      : '/';

    navigate(normalizePostLoginTarget(target), { replace: true });
  }, [capabilities, location.state, nav, navigate, props?.from, props?.inline, shouldForceTableopsLanding, user]);

  const handleGoogleLogin = () => {
    setErrorMsg(null);
    setOidcPending(true);
    // Canonical OIDC entrypoint lives under /auth/* (gateway passthrough).
    // Using this stable path avoids environment-specific proxy prefixes.
    window.location.assign('/auth/oidc/google/start');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setSubmitting(true);
    try {
      const loginPayload = { username, password };

      try {
        await bff.post<void, { username: string; password: string }>(
          '/auth/login',
          loginPayload,
          { timeoutMs: 60_000 }
        );
      } catch (err) {
        const msg = String((err as Error)?.message || '').toLowerCase();
        const isTimeout = msg.includes('timed out') || msg.includes('timeout');
        if (!isTimeout) throw err;

        // One bounded retry helps absorb occasional cold starts in Cloud Run.
        await bff.post<void, { username: string; password: string }>(
          '/auth/login',
          loginPayload,
          { timeoutMs: 60_000 }
        );
      }

      // Immediately pull fresh session (user/capabilities/nav).
      // Force-bypass the auth refresh cooldown: we just established a new
      // session and MUST re-read /auth/me now, otherwise the SPA bounces
      // back to this login screen.
      await refresh(true);

      // Tell other tabs to re-check /bff/auth/me and auto-recover.
      postAuthEvent('login');

      // Keep any guards using react-query in sync
      await queryClient.invalidateQueries({ queryKey: ['me'] });

      // We are authenticated now; clear any persisted kickout banner.
      clearAuthKickoutState();
      resetAuthDisconnected();

      // If rendered inline (on a protected page), keep the URL as-is.
      // If rendered on /login, navigate back to the original location.
      const from =
        (location.state as any)?.from ||
        props?.from ||
        getAuthKickoutState()?.from;

      const target = typeof from?.pathname === 'string'
        ? from.pathname + (from.search || '') + (from.hash || '')
        : '/';

      if (!props?.inline) {
        navigate(normalizePostLoginTarget(target), { replace: true });
      }
    } catch (err: any) {
      setErrorMsg(authUiMessageFromError(err, { action: 'login' }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <style>{`
        .login-input:-webkit-autofill,
        .login-input:-webkit-autofill:hover,
        .login-input:-webkit-autofill:focus,
        .login-input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 1000px rgb(23 23 23) inset !important;
          -webkit-text-fill-color: rgb(245 245 245) !important;
          caret-color: rgb(245 245 245) !important;
          transition: background-color 9999s ease-out 0s;
        }
      `}</style>
    <div
      onClick={props.inline ? props.onClose : undefined}
      className={
        props.inline
          ? 'fixed inset-0 z-[1000] overflow-auto bg-black/80 backdrop-blur-[1.4px] flex items-center justify-center p-4'
          : 'h-screen overflow-auto bg-black/80 backdrop-blur-[1.4px] flex items-center justify-center'
      }
    >
      <div
        onClick={props.inline ? (e) => e.stopPropagation() : undefined}
        className="relative w-full max-w-sm p-6 bg-black/60 border border-neutral-800 text-neutral-100 rounded-md backdrop-blur-none"
      >
        {props.inline && props.onClose && (
          <button
            type="button"
            onClick={props.onClose}
            aria-label="Close login"
            className="absolute right-2 top-2 inline-flex items-center justify-center rounded-md p-1.5 text-neutral-400 hover:text-neutral-200 hover:bg-neutral-800/80 transition-colors"
          >
            <X size={14} />
          </button>
        )}
        <h1 className="text-2xl mb-4 text-center">Login</h1>
        {errorMsg && (
          <div className="mb-3 rounded-md  px-3 py-2 text-center">
            <p className="text-red-500 font-semibold">{errorMsg.title}</p>
            <p className="text-red-400 text-sm">{errorMsg.description}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div>
            <label htmlFor="user" className="block text-sm mb-1">Username</label>
            <input
              id="user"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="login-input w-full px-3 py-1.5 rounded-md pac-glass-input placeholder-neutral-500 focus:outline-none disabled:opacity-50"
              placeholder="Enter your username"
              autoComplete="username"
              spellCheck={false}
              required
              disabled={submitting}
            />
          </div>

          <div>
            <label htmlFor="pass" className="block text-sm mb-1">Password</label>
            <input
              id="pass"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="login-input w-full px-3 py-1.5 rounded-md pac-glass-input placeholder-neutral-500 focus:outline-none disabled:opacity-50"
              placeholder="Enter your password"
              autoComplete="current-password"
              required
              disabled={submitting}
            />
          </div>

          <button
            type="submit"
            disabled={submitting || !username || !password}
            className="w-full bg-transparent border border-neutral-700 hover:border-emerald-500 hover:text-emerald-400 px-4 py-1.5 rounded-md text-white font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            aria-busy={submitting}
          >
            {submitting ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        {/* ── OIDC divider ── */}
        <div className="mt-5 flex items-center gap-3">
          <div className="flex-1 h-px bg-neutral-800" />
          <span className="text-xs text-neutral-500 select-none">or</span>
          <div className="flex-1 h-px bg-neutral-800" />
        </div>

        <button
          type="button"
          onClick={handleGoogleLogin}
          disabled={oidcPending || submitting}
          className="mt-3 w-full flex items-center justify-center gap-2.5 bg-transparent border border-neutral-700 hover:border-sky-500 hover:text-sky-300 px-4 py-1.5 rounded-md text-neutral-200 text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          aria-busy={oidcPending}
        >
          {/* Google "G" icon (SVG, no external dependency) */}
          <svg width="16" height="16" viewBox="0 0 18 18" fill="none" aria-hidden="true">
            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z" fill="#4285F4"/>
            <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z" fill="#34A853"/>
            <path d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332Z" fill="#FBBC05"/>
            <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58Z" fill="#EA4335"/>
          </svg>
          {oidcPending ? 'Redirecting…' : 'Continue with Google'}
        </button>
      </div>
    </div>
    </>
  );
}
